import type { PAGE_LABELS } from "@src/constants/page-labels";
import { useLabelResolutionInputs } from "@src/hooks/useLabelResolutionInputs";
import { resolvePageLabels } from "@src/utils/page-labels-resolver";
import { useMemo } from "react";

type PageLabelMap = typeof PAGE_LABELS;

/**
 * Resolves page labels for `page` using the current user's role and
 * FEATURE_LABEL flag. Memoized — re-runs only when role, flag, or age changes.
 *
 * Age-based label voice (18+ → first-person "self", under 18 → third-person
 * "patient") only applies to the `patient` (self-booking) role. Navigator and
 * referrer flows always keep their role-based third-person copy. The age is
 * taken from the explicit `options.age` when provided, otherwise derived from
 * the patient's date of birth in ApplicationContext.
 *
 * @param page - Key from PAGE_LABELS (e.g. "getStartedForm")
 * @param options.age - Optional patient age; takes priority over the derived DOB age
 */
export function useResolvedPageLabels<P extends keyof PageLabelMap>(
  page: P,
  options?: { age?: number },
) {
  const { role, enabled, age } = useLabelResolutionInputs(options);

  return useMemo(() => resolvePageLabels(page, role, { enabled, age }), [page, role, enabled, age]);
}
